<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1" v-if="baseReservation && baseReservation.titles"><b>{{ $t('reservationIntro') }}</b> {{baseReservation.titles[currentLanguage.key]}}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <div class="d-flex flex-wrap mb-3">
                    <div class="d-flex flex-column col-12 col-lg-6">
                        <!-- Image -->
                        <div role="group" class="form-group">
                        <label class=""> {{ $t('imageMandatory') }}</label>
                            <GalleryPicker class="gallery-picker" v-model="reservation.image" :ratioWidth="1" :ratioHeight="1" :max="1" v-bind:class="{ 'is-invalid': genericValidator(reservation.image, attemptedSubmit) === false || errorValidator('Image') === false }"></GalleryPicker>
                        </div>
                    </div>
                    <div class="d-flex flex-column col-12 col-lg-6">
                        <!-- Titles -->
                        <div role="group" class="form-group">
                        <label class=""> {{ $t('titleMandatory') }}</label>
                            <CInput class="m-0" v-model="reservation.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(reservation.titles, attemptedSubmit) && errorValidator('Titles')"></CInput>
                        </div>
                        <!-- CallToAction -->
                        <div role="group" class="form-group">
                        <label class=""> {{ $t('callToActionsMandatory') }}</label>
                            <CInput class="m-0" type="text" v-model="reservation.callToActions[currentLanguage.key]" :invalid-feedback="errorOutput['CallToActions']" :is-valid="genericValidator(reservation.callToActions, attemptedSubmit) && errorValidator('CallToActions')"></CInput>
                        </div>
                        <!-- Contents -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('contentsMandatory') }}</label>
                            <vue-editor v-model="reservation.contents[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(reservation.contents[currentLanguage.key], attemptedSubmit) == false || errorValidator('Contents') }"></vue-editor>
                            <div v-if="errorOutput['Contents']" class="invalid-feedback">{{errorOutput['Contents']}}</div>
                        </div>
                        <!-- files (not required) -->
                        <div role="group" class="form-group">
                            <FilesPicker v-model="reservation.files" :title="$t('Files')" v-bind:class="{ 'is-invalid': errorValidator('Files') === false }" :invalidFeedback="errorOutput['Files']"></FilesPicker>
                        </div>
                        <div class="d-flex gap-1">
                            <!-- Form -->
                            <div role="group" class="form-group">
                                <label> {{ $t('form') }}</label>
                                <CInputCheckbox :checked="reservation.form" @update:checked="reservation.form = !reservation.form" :invalid-feedback="errorOutput['Form']" :is-valid="errorValidator('Form')"></CInputCheckbox>
                            </div>
                            <!-- StoreLinks -->
                            <div role="group" class="form-group ">
                                <label> {{ $t('storeLinks') }}</label>
                                <CInputCheckbox :checked="reservation.storeLinks" @update:checked="reservation.storeLinks = !reservation.storeLinks" :invalid-feedback="errorOutput['StoreLinks']" :is-valid="errorValidator('StoreLinks')"></CInputCheckbox>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { API_HELPER } from "@/helpers/api.js";
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import Reservation from "@/classes/Reservation";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FilesPicker from '@/controls/FilesPicker.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { VueEditor } from "vue2-editor";


  export default {
        components: { LanguageDropdown, ConfirmationPopUp, FilesPicker, GalleryPicker, FormLoading, VueEditor },
    props:{    
        baseReservation:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            reservation: new Reservation({},{},{},[]),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
        }
    },
    mounted: function()
    {
        this.resetReservation();
    },
    computed:
    {
        ...mapState('reservations', ['putReservationOutput']),
        ...mapState('reservations', ['postReservationOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.reservation.titles)) { if(!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.reservation.contents)) { if(!value) { result['Contents'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.reservation.callToActions)) { if(!value) { result['CallToActions'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        name: function () {
            return this.baseReservation || this.baseName ? (this.baseReservation ? this.baseReservation.name : this.baseName) : null;
        }
    },
    methods:{
        genericValidator,
        errorValidator,
         resetReservation(){
            if(!this.baseReservation)
            {
                var titles = {}; var contents = {}; var callToActions = {}; 
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key] = ""; contents[language.key] = ""; callToActions[language.key] = "";  });
                this.reservation = new Reservation(titles, contents, callToActions, []);
            }
            else
            {
                this.reservation = deepClone(new Reservation, this.baseReservation);  

                // Image
                this.reservation.image = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.reservation.imagePath]), path: this.reservation.imagePath}
                
                // File
                var files = [];
                this.reservation.filesPath.forEach(function(file)
                {
                    files.push({path: file});
                });
                this.reservation.files = files;

            }
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.reservation.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.reservation);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: 'Reservations'});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putReservationOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postReservationOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseReservation: function()
        {
            this.resetReservation();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}
</style> 